import MaterialTable, { MTableToolbar } from '@material-table/core'
import { Button, Typography, MenuItem } from '@material-ui/core'
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles'
import React from 'react'
import { useSelector } from 'react-redux'
import { LINE_ITEM_TYPES, formatNumsToDollars, BOARDING, DAYCARE } from '../../utils'
import MoreMenu from '../Common/Menus/MoreMenu'

import { tableAlternateLight } from '../../services/theme'

const useStyles = makeStyles((theme) => ({
	toolbar: {
		paddingLeft: 0
	},
	actionButton: {
		fontSize: '1rem',
		fontWeight: 400,
		lineHeight: '22px',
		color: '#6B4AD6',
		textTransform: 'none',
		minWidth: '35px',
		alignItems: 'center',
		padding: '4px',
		marginLeft: '1rem'
	},
	subtext: {
		fontSize: '12px',
		fontStyle: 'italic',
		fontWeight: 400,
		lineHeight: '16px',
		color: '#777679'
	},
	strikeThrough: {
		marginRight: '8px',
		color: '#A2A1A4',
		textDecorationLine: 'line-through',
		fontWeight: '400',
		fontSize: '14px'
	},
	extendedPickup: {
		fontWeight: '400',
		borderBottom: 'none',
		width: 'calc((100% - 0px) / 3)',
		textAlign: 'right',
		fontSize: '14px'
	}
}))

const InvoiceTable = (props) => {
	const classes = useStyles()
	let {
		setIsEditItemPopupShown,
		setSelectedItem,
		facility,
		setEditPromo,
		setDeletePromo,
		showTitle = true,
		showToolbar = true
	} = props
	const invoiceLoading = useSelector((state) => state.specific.visits.invoiceLoading)
	let { visit } = props

	const paymentsProcessed = !!visit?.lineItems?.find((i) => !!i.stripe_charge_status && i.type !== 'deposit')


	return (
		<MuiThemeProvider theme={tableAlternateLight}>
			<MaterialTable
				isLoading={invoiceLoading}
				style={{
					borderRadius: '0px',
					boxShadow: 'none',
					borderBottom: '1px solid #D0CFD2',
					marginBottom: '16px'
				}}
				options={{
					headerStyle: {
						color: '#1A1A35',
						textTransform: 'uppercase',
						fontWeight: 800,
						fontSize: '12px',
						lineHeight: '16px',
						padding: '8px 0px',
						borderBottom: '1px solid #D0CFD2'
					},
					actionsColumnIndex: -1,
					rowStyle: {
						verticalAlign: 'top',
						backgroundColor: '#FFFFFF'
					},
					toolbarButtonAlignment: 'left',
					searchFieldAlignment: 'left',
					paging: false,
					search: false,
					actionsCellStyle: {
						border: 'none',
						padding: '6px 0px'
					}
				}}
				localization={{
					header: {
						actions: ''
					}
				}}
				actions={[{}]}
				columns={[
					{
						title: 'Description',
						field: 'description',
						render: (data) => {
							const { description = '', subtext = null } = data
							return (
								<div>
									<Typography variant="caption">{description}</Typography>
									{subtext ? (
										<Typography className={classes.subtext}>{subtext}</Typography>
									) : null}
								</div>
							)
						},
						cellStyle: {
							borderBottom: 'none',
							padding: '9px 0px'
						}
					},
					{
						title: 'Qty',
						field: 'quantity',
						type: 'numeric',
						render: (data) => {
							const { description = '', type = '', quantity } = data
							if (description.includes('Bank')) {
								return null
							}
							if (
								type === LINE_ITEM_TYPES.VisitPromo ||
								type === LINE_ITEM_TYPES.PreLineItemAppliedPromo ||
								type === LINE_ITEM_TYPES.HouseholdCredit ||
								type === LINE_ITEM_TYPES.AddonBank
							) {
								return null
							}
							return `${quantity}`
						},
						cellStyle: {
							borderBottom: 'none',
							padding: '9px 50px 9px 0px'
						},
						headerStyle: { padding: '9px 50px 9px 0px' }
					},
					{
						title: 'Price',
						field: 'price',
						render: (data) => {
							const { description = '', type = '', price, quantity } = data
							if (
								type === LINE_ITEM_TYPES.VisitPromo ||
								type === LINE_ITEM_TYPES.PreLineItemAppliedPromo ||
								type === LINE_ITEM_TYPES.HouseholdCredit ||
								type === LINE_ITEM_TYPES.AddonBank
							) {
								return ''
							}
							if (description.includes('Daycare')) {
								return `${quantity} Credit${quantity > 1 ? 's' : ''}`
							}
							if (description.includes('Credit Bank')) {
								return (
									<Typography
										style={{ fontSize: 14, fontStyle: 'italic' }}>{`-${quantity} Credit${quantity > 1 ? 's' : ''
											}`}</Typography>
								)
							}
							if (description.includes('Bank')) {
								return ''
							}

							return formatNumsToDollars(price)
						},
						cellStyle: {
							borderBottom: 'none',
							padding: '9px 0px'
						}
					},
					{
						title: 'Upcharge',
						field: 'upcharge',
						render: (data) => {
							const { upcharge = 0, type = '' } = data
							if (
								type === LINE_ITEM_TYPES.VisitPromo ||
								type === LINE_ITEM_TYPES.PreLineItemAppliedPromo ||
								type === LINE_ITEM_TYPES.HouseholdCredit ||
								type === LINE_ITEM_TYPES.AddonBank
							) {
								return ''
							}
							return formatNumsToDollars(upcharge)
						},
						cellStyle: {
							borderBottom: 'none',
							padding: '9px 0px'
						}
					},
					{
						title: 'Amount',
						field: 'amount',
						render: (data) => {
							let price = (((data.price + (data.upcharge || 0)) * data.quantity) / 100).toFixed(2)
							let extendedVisitDefaultPrice

							let type = 'daycare'

							if (facility && facility.services && facility.services[type]) {
								extendedVisitDefaultPrice = facility.services.daycare.late_pickup_fee || 0

								extendedVisitDefaultPrice = (extendedVisitDefaultPrice / 100).toFixed(2)
							}
							let differenceInPrice = extendedVisitDefaultPrice !== price

							return differenceInPrice && data.type === 'extended_pickup' ? (
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<Typography className={classes.strikeThrough}>
										${extendedVisitDefaultPrice}
									</Typography>
									<Typography className={classes.extendedPickup}>${price}</Typography>
								</div>
							) : (
								`$${price}`
							)
						},
						cellStyle: {
							borderBottom: 'none',
							padding: '9px 0px'
						}
					}
				]}
				components={{
					Toolbar: (props) =>
						showToolbar ? (
							<MTableToolbar
								{...props}
								classes={{
									root: classes.toolbar
								}}
							/>
						) : null,
					Action: (props) => {
						const { data } = props
						const { _id, type, stripe_charge_status } = data
						const isTip = props.data.description?.includes('Tip')
						const noStripeChargeStatus = !stripe_charge_status


						if (
							!paymentsProcessed &&
							(type === LINE_ITEM_TYPES.VisitPromo ||
								type === LINE_ITEM_TYPES.PreLineItemAppliedPromo)
						) {
							return (
								<div className={classes.actionButton}>
									<MoreMenu
										options={[
											<MenuItem key="edit-promo" onClick={() => setEditPromo(true)}>
												Edit Item
											</MenuItem>,
											<MenuItem key="remove-promo" onClick={() => setDeletePromo(true)}>
												Remove Item
											</MenuItem>
										]}
									/>
								</div>
							)
						}
						return (!paymentsProcessed && _id && noStripeChargeStatus) ||
							(isTip && noStripeChargeStatus && !paymentsProcessed) ? (
							<Button
								variant="text"
								className={classes.actionButton}
								onClick={() => {
									setSelectedItem(data)
									setIsEditItemPopupShown(true)
								}}>
								Edit
							</Button>
						) : null
					}
				}}
				data={props.invoice}
				title={showTitle ? (visit?.show_old_invoice ? 'INVOICE - OLD SYSTEM' : 'INVOICE') : null}
			/>
		</MuiThemeProvider>
	)
}

export default InvoiceTable
